import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Card, CardHeader } from '@/ui/Card';
import { Field, Input } from '@/ui/Field';
import { EstadoError } from '@/components/EstadoError';
import {
  useGenerarDominio, useGuardarMarca, useMarca, type InstruccionesDeDominio,
} from './api';

/**
 * Dominio y SSL — el asistente que redacta los pasos.
 *
 * No ejecuta nada en el servidor: genera el registro DNS, los comandos y el
 * aviso de firewall para publicar el portal con HTTPS. Eso era verdad en el
 * vanilla y sigue siéndolo.
 *
 * ## Lo que cambia
 *
 * - **Los comandos ya no se copian desde un atributo `onclick`.** Se metían
 *   dentro de una plantilla JavaScript escapando solo las comillas invertidas
 *   (`cmds.replace(/`/g,'\\`')`) y se pegaban en el HTML: un `${` en cualquier
 *   comando —o un `"`— rompía el botón, y el `<pre>` escapaba solo los `<`.
 * - **El bloque de comandos es un bloque de comandos.** Tiene su propia región
 *   con nombre, se puede seleccionar con el teclado y el botón de copiar avisa
 *   de que copió.
 * - **Guardar el dominio ya no depende de una cadena interpolada en el HTML**
 *   (`saveDomain('${r.app_url}')`).
 */
export function Dominio() {
  const marca = useMarca(true);
  const generar = useGenerarDominio();

  const b = marca.data;
  const [dominio, setDominio] = useState('');
  const [correo, setCorreo] = useState('');
  const [ip, setIp] = useState('');
  const [listo, setListo] = useState(false);

  if (!listo && b) {
    setDominio((b.app_url ?? '').replace(/^https?:\/\//, ''));
    setListo(true);
  }

  if (marca.isError) {
    return (
      <EstadoError
        err={marca.error}
        que="la configuración del dominio"
        onReintentar={() => void marca.refetch()}
      />
    );
  }

  return (
    <Card className="max-w-3xl">
      <CardHeader
        titulo={<><i className="bi bi-globe2" aria-hidden="true" /> Asistente de dominio y SSL</>}
      />
      <div className="flex flex-col gap-4 p-4">
        <p className="flex items-start gap-2 rounded-sm border border-info bg-info/8 p-3 text-2 text-info-ink">
          <i className="bi bi-info-circle mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            Este asistente <strong>no ejecuta nada en el servidor</strong>: te genera los pasos y
            la configuración exactos para publicar el portal en tu dominio con HTTPS.
          </span>
        </p>

        <p className="rounded-sm border border-line bg-sunken p-3 text-3">
          <i className="bi bi-broadcast" aria-hidden="true" />{' '}
          <strong>Dominio activo (APP_URL):</strong>{' '}
          {b?.app_url
            ? <span className="text-ok-ink">{b.app_url}</span>
            : <span className="text-muted">
              usando el del entorno: {b?.env_app_url || 'no definido'}
            </span>}
        </p>

        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            generar.mutate(
              { domain: dominio.trim(), le_email: correo.trim(), server_ip: ip.trim() },
              { onError: (err) => Utils.toast((err as Error).message, 'error') });
          }}
        >
          <h3 className="text-3 font-bold text-brand-ink">
            <i className="bi bi-1-circle-fill" aria-hidden="true" /> Datos de tu dominio
          </h3>

          <Field
            label="Dominio que vas a usar"
            ayuda="El nombre completo con el que entrarán al portal, sin «https://». Por ejemplo intranet.gruposuservicio.com"
          >
            <Input
              value={dominio}
              onChange={(e) => setDominio(e.target.value)}
              placeholder="intranet.tuempresa.com"
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Correo para Let's Encrypt"
              ayuda="Ahí llegan los avisos de vencimiento del certificado."
            >
              <Input
                type="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                placeholder="ti@tuempresa.com"
              />
            </Field>
            <Field
              label="IP pública del servidor"
              opcional
              ayuda="La IP donde corre Docker. Hace falta para el registro DNS; si no la sabes, pregúntala a tu proveedor."
            >
              <Input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="190.xx.xx.xx" />
            </Field>
          </div>

          <p>
            <Button
              type="submit"
              loading={generar.isPending}
              disabled={!dominio.trim() || !correo.trim()}
              razonDeshabilitado="Escribe el dominio y el correo."
            >
              <i className="bi bi-magic" aria-hidden="true" /> Generar instrucciones
            </Button>
          </p>
        </form>

        {generar.data && <Instrucciones datos={generar.data} />}
      </div>
    </Card>
  );
}

function Instrucciones({ datos: r }: { datos: InstruccionesDeDominio }) {
  const guardar = useGuardarMarca();
  const [copiado, setCopiado] = useState(false);
  const comandos = r.commands.join('\n');

  return (
    <section className="flex flex-col gap-4 border-t border-line pt-4">
      <div>
        <h3 className="mb-1 text-3 font-bold text-brand-ink">
          <i className="bi bi-2-circle-fill" aria-hidden="true" /> Crea este registro DNS
        </h3>
        <p className="mb-2 text-2 text-muted">{r.dns_record.nota}</p>
        <div className="overflow-x-auto rounded-sm border border-line">
          <table className="w-full text-2">
            <caption className="sr-only">El registro DNS que hay que crear</caption>
            <thead className="bg-sunken">
              <tr>
                <th scope="col" className="p-2 text-left font-semibold">Tipo</th>
                <th scope="col" className="p-2 text-left font-semibold">Nombre</th>
                <th scope="col" className="p-2 text-left font-semibold">Valor (apunta a)</th>
                <th scope="col" className="p-2 text-left font-semibold">TTL</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-line">
                <td className="p-2 font-semibold">{r.dns_record.tipo}</td>
                <td className="p-2 font-mono">{r.dns_record.nombre}</td>
                <td className="p-2 font-mono">{r.dns_record.valor}</td>
                <td className="p-2">{r.dns_record.ttl}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="mb-1 text-3 font-bold text-brand-ink">
          <i className="bi bi-3-circle-fill" aria-hidden="true" /> Firewall
        </h3>
        <p className="flex items-start gap-2 rounded-sm border border-warn bg-warn/10 p-3 text-2 text-warn-ink">
          <i className="bi bi-shield mt-0.5 shrink-0" aria-hidden="true" />
          <span>{r.firewall}</span>
        </p>
      </div>

      <div>
        <h3 className="mb-1 text-3 font-bold text-brand-ink">
          <i className="bi bi-4-circle-fill" aria-hidden="true" /> Ejecuta en el servidor
        </h3>
        <p className="mb-2 text-2 text-muted">
          En la terminal del servidor, dentro de la carpeta del proyecto.
        </p>
        <div className="relative">
          {/* El bloque es su propia región con nombre y `tabIndex={0}`: así se
              puede recorrer y seleccionar sin ratón. En el vanilla era un `<pre>`
              suelto que solo escapaba los `<`. */}
          <pre
            tabIndex={0}
            role="group"
            aria-label="Comandos para el servidor"
            className="overflow-x-auto rounded-sm bg-brand-ink p-4 text-2 leading-relaxed text-on-brand focus-visible:focus-anillo"
          >
            {comandos}
          </pre>
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-2 top-2 bg-surface/10 text-on-brand hover:bg-surface/20"
            onClick={() => {
              void navigator.clipboard.writeText(comandos).then(
                () => {
                  setCopiado(true);
                  Utils.toast('Comandos copiados', 'success');
                  window.setTimeout(() => setCopiado(false), 2000);
                },
                () => Utils.toast('El navegador no dejó copiar. Selecciónalos a mano.', 'error'),
              );
            }}
          >
            <i className={`bi ${copiado ? 'bi-check2' : 'bi-clipboard'}`} aria-hidden="true" />
            {copiado ? 'Copiados' : 'Copiar'}
          </Button>
        </div>
      </div>

      <div>
        <h3 className="mb-1 text-3 font-bold text-brand-ink">
          <i className="bi bi-5-circle-fill" aria-hidden="true" /> Activar en el portal
        </h3>
        <p className="mb-2 text-2 text-muted">{r.final}</p>
        <Button
          loading={guardar.isPending}
          onClick={() => guardar.mutate({ app_url: r.app_url }, {
            onSuccess: () => Utils.toast(
              `Dominio guardado. Los correos y certificados usarán ${r.app_url}`, 'success'),
            onError: (e) => Utils.toast((e as Error).message, 'error'),
          })}
        >
          <i className="bi bi-check-circle" aria-hidden="true" /> Guardar dominio: {r.app_url}
        </Button>
      </div>
    </section>
  );
}
